/* voyado-collection.js
 * Uses window.elevate.query.c for cluster/market/locale/session; reads window.VOYADO_COLLECTION_CONFIG for currency/collectionName/shopUrl/pageSize.
 * Fetches Voyado landing-page API and renders products + facets into the DOM.
 */

const cfg = window.VOYADO_COLLECTION_CONFIG ?? {};
// The Elevate SDK (window.elevate) handles all request plumbing — cluster URL,
// market/locale/touchpoint, and customerKey/sessionKey (incl. the logged-in key
// via its session handler) are auto-attached to every query.* / notify.* call.
const CURRENCY = cfg.currency ?? "NOK";
const COLL_NAME = cfg.collectionName ?? "";
const SHOP_URL = cfg.shopUrl ?? "";
const PAGE_SIZE = cfg.pageSize ?? 24;
// Editorial tiles from the section's `collection_tile` blocks, injected into
// the client-rendered grid at their configured 1-based position.
const TILES = Array.isArray(cfg.tiles) ? cfg.tiles.filter((t) => t?.position) : [];

const EMPTY_TEXT =
  cfg.emptyText ?? "Ingen produkter funnet i denne kolleksjonen.";
const ERROR_TEXT = cfg.errorText ?? "Noe gikk galt. Last siden på nytt.";

// Fallback only — real order/labels/default come from data.primaryList.sort
// on every response, since Elevate owns the sort configuration.
const SORT_OPTIONS_FALLBACK = [
  { id: "NEWEST_FIRST", label: "Nytt" },
  { id: "RELEVANCE", label: "Mest relevant" },
  { id: "PRICE_DESCENDING", label: "Høyest pris" },
  { id: "PRICE_ASCENDING", label: "Lavest pris" },
  { id: "DISCOUNT", label: "Rabatt" },
];

const CHEVRON_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true" focusable="false"><polyline points="6 9 12 15 18 9"></polyline></svg>`;

const PLUS_SVG = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;

class VoyadoCollection extends HTMLElement {
  #grid = null;
  #pageRef = location.pathname;
  #skip = 0;
  #done = false;
  #seq = 0;
  #observer = null;
  #sentinel = null;
  #facets = [];
  #hits = 0;
  #sort = null;
  #sortOptions = SORT_OPTIONS_FALLBACK;
  #defaultSort = SORT_OPTIONS_FALLBACK[0].id;
  #active = {};
  #rendered = 0;
  #tiles = TILES;
  #onPopState = null;
  #onPillsClick = null;
  #drawerBody = null;
  #onDrawerChange = null;
  #onDrawerFocusout = null;
  #onDrawerKeydown = null;
  #disconnected = false;

  async connectedCallback() {
    await window.voyadoElevateReady;
    if (this.#disconnected) return;
    if (!window.elevate) {
      console.warn("Voyado: window.elevate not available");
      return;
    }
    this.#grid = this.querySelector("[data-voyado-grid]");
    if (!this.#grid) return;
    this.#pageRef = location.pathname.replace(/^\/(collections|pages)/, "");
    this.#readURL(new URLSearchParams(location.search));
    this.#bindEvents();
    this.#fetch();
  }

  disconnectedCallback() {
    this.#disconnected = true;
    window.removeEventListener("popstate", this.#onPopState);
    document
      .getElementById("voyado-active-filters")
      ?.removeEventListener("click", this.#onPillsClick);
    this.#drawerBody?.removeEventListener("change", this.#onDrawerChange);
    this.#drawerBody?.removeEventListener("focusout", this.#onDrawerFocusout);
    this.#drawerBody?.removeEventListener("keydown", this.#onDrawerKeydown);
    this.#observer?.disconnect();
  }

  // ── URL ──────────────────────────────────────────────────────────────────

  #readURL(p) {
    this.#active = {};
    this.#sort = null;
    for (const [k, v] of p) {
      if (k === "sort") {
        this.#sort = v || null;
        continue;
      }
      if (!k.startsWith("f.")) continue;
      const fk = k.slice(2);
      if (fk.endsWith(".min") || fk.endsWith(".max")) {
        const id = fk.slice(0, -4);
        const bound = fk.slice(-3);
        if (Array.isArray(this.#active[id])) this.#active[id] = {};
        (this.#active[id] ??= {})[bound] = Number(v);
      } else {
        this.#active[fk] = v.split("|").filter(Boolean);
      }
    }
  }

  #pushURL() {
    const p = new URLSearchParams();
    for (const [id, val] of Object.entries(this.#active)) {
      if (Array.isArray(val) && val.length) p.set(`f.${id}`, val.join("|"));
      else if (val && !Array.isArray(val)) {
        if (val.min != null) p.set(`f.${id}.min`, val.min);
        if (val.max != null) p.set(`f.${id}.max`, val.max);
      }
    }
    if (this.#sort && this.#sort !== this.#defaultSort) p.set("sort", this.#sort);
    const qs = p.toString();
    history.pushState(
      {},
      "",
      qs ? `${location.pathname}?${qs}` : location.pathname
    );
  }

  // Builds the landingPage params. #active is already in the library's facet
  // shape: { id: string[] } for multi-select, { id: { min, max } } for ranges.
  // market/locale/touchpoint/customerKey/sessionKey are auto-attached by the SDK.
  #queryParams() {
    const facets = {};
    for (const [id, val] of Object.entries(this.#active)) {
      if (Array.isArray(val)) {
        if (val.length) facets[id] = val;
      } else if (val && (val.min != null || val.max != null)) {
        facets[id] = val;
      }
    }
    const params = {
      pageReference: this.#pageRef,
      skip: this.#skip,
      limit: PAGE_SIZE,
      facets,
    };
    if (this.#sort && this.#sort !== this.#defaultSort) params.sort = this.#sort;
    return params;
  }

  // ── Fetch ─────────────────────────────────────────────────────────────────

  async #fetch() {
    if (this.#done) return;
    // The library has no request abort, so tag each request and only render the
    // latest — drops stale responses from rapid filter/sort/paging changes.
    const seq = ++this.#seq;
    try {
      const data = await window.elevate.query.landingPage(this.#queryParams());
      if (seq !== this.#seq) return; // superseded by a newer request
      this.#render(data);
    } catch (e) {
      if (seq !== this.#seq) return;
      console.error("Voyado collection:", e);
      this.#showError();
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  #render(data) {
    if (this.#skip === 0) {
      // Atomically replace old content now that new results are ready (no layout shift)
      this.#grid.innerHTML = "";
      this.#hits = data.primaryList?.totalHits ?? 0;
      this.#facets = data.primaryList?.facets ?? [];
      const sortData = data.primaryList?.sort ?? data.sort;
      if (sortData?.options?.length) {
        this.#sortOptions = sortData.options;
        this.#defaultSort = sortData.selected ?? this.#sortOptions[0].id;
      }
      this.#renderFilters();
    }
    this.#grid.removeAttribute('data-loading');
    this.#removeSentinel();

    const groups = data.primaryList?.productGroups ?? [];
    if (!groups.length && this.#skip === 0) {
      this.#showEmpty();
      return;
    }

    const frag = document.createDocumentFragment();
    for (const g of groups) {
      const p = g.products?.[0];
      if (!p) continue;
      const position = this.#rendered + 1;
      for (const tile of this.#tiles) {
        if (tile.position !== position) continue;
        // .collection-tile is itself the grid item (grid-column/-row + stretch-to-fill
        // for its absolutely-positioned children) — must be a direct grid child, not
        // wrapped in an <li> like product cards, or it collapses to zero height.
        const tpl = document.createElement("template");
        tpl.innerHTML = this.#tileCard(tile);
        frag.appendChild(tpl.content.firstElementChild);
      }
      const li = document.createElement("li");
      li.innerHTML = this.#card(p, this.#rendered++);
      frag.appendChild(li);
    }
    this.#grid.appendChild(frag);
    this.#skip += PAGE_SIZE;
    if (groups.length < PAGE_SIZE) this.#done = true;
    else this.#addSentinel();
  }

  // ── Filters ───────────────────────────────────────────────────────────────

  #renderFilters() {
    const body = document.getElementById("filter-drawer-body");
    if (!body) return;

    // Preserve which groups are open across re-renders
    const openGroups = new Set(
      [...body.querySelectorAll('[data-filter-group][data-open]')]
        .map(g => g.dataset.groupId)
        .filter(Boolean)
    );
    const scrollTop = body.scrollTop;

    if (!this.#facets.length) {
      body.innerHTML = "";
    } else {
      let h = this.#sortGroupHTML();
      for (const f of this.#facets) {
        h += this.#facetGroupHTML(f);
      }
      body.innerHTML = h;
    }

    // Restore open/closed state (sort group defaults open on first render when openGroups is empty)
    body.querySelectorAll('[data-filter-group][data-group-id]').forEach(g => {
      const id = g.dataset.groupId;
      const toggle = g.querySelector('[data-toggle]');
      const shouldBeOpen = openGroups.size > 0 ? openGroups.has(id) : id === '__sort__';
      if (shouldBeOpen) {
        g.setAttribute('data-open', '');
        toggle?.setAttribute('aria-expanded', 'true');
      } else {
        g.removeAttribute('data-open');
        toggle?.setAttribute('aria-expanded', 'false');
      }
    });
    body.scrollTop = scrollTop;

    // Update product count
    const countEl = document.querySelector("[data-voyado-hits]");
    if (countEl) {
      countEl.textContent =
        this.#hits > 0
          ? `${this.#hits} produkt${this.#hits !== 1 ? "er" : ""}`
          : "";
    }

    // Update active pills + badge
    this.#renderActivePills();
    this.#updateFilterBadge();

    // Wire clear-all button in drawer footer
    const clearAll = document.getElementById("filter-clear-all");
    if (clearAll) {
      clearAll.hidden = !this.#hasActive();
      clearAll.onclick = () => {
        this.#active = {};
        this.#sort = null;
        this.#apply();
      };
    }
  }

  #sortGroupHTML() {
    const cur = this.#sort ?? this.#defaultSort;
    return `<div class="filter-group" data-filter-group data-group-id="__sort__" data-open>
      <button class="filter-group__toggle" type="button" data-toggle aria-expanded="true">
        <span>Sorter</span>
        ${CHEVRON_SVG}
      </button>
      <div class="filter-group__values filter-group__values--radio">
        ${this.#sortOptions.map(
          (o) => `<label class="filter-radio">
          <input type="radio" name="voyado-sort" value="${this.#escA(o.id)}" ${o.id === cur ? "checked" : ""} data-filter-type="sort">
          <span>${this.#esc(o.label)}</span>
        </label>`
        ).join("")}
      </div>
    </div>`;
  }

  #facetGroupHTML(facet) {
    const id = String(facet.id ?? facet.attribute ?? facet.label ?? "");
    const label = String(facet.label ?? id);
    const type = String(facet.type ?? "list").toLowerCase();
    const av = this.#active[id];
    const cnt = Array.isArray(av) ? av.length : 0;

    let inner;
    if (type === "range") {
      const rv = !Array.isArray(av) && av ? av : {};
      inner = `<div class="filter-price">
        <div class="filter-price__field">
          <label class="filter-price__label">Fra
            <input class="filter-price__input" type="number" data-filter-type="range" data-facet-id="${this.#escA(id)}" data-range-bound="min" value="${rv.min ?? ""}" placeholder="${this.#escA(String(facet.min ?? "0"))}">
          </label>
        </div>
        <span class="filter-price__dash">–</span>
        <div class="filter-price__field">
          <label class="filter-price__label">Til
            <input class="filter-price__input" type="number" data-filter-type="range" data-facet-id="${this.#escA(id)}" data-range-bound="max" value="${rv.max ?? ""}" placeholder="${this.#escA(String(facet.max ?? ""))}">
          </label>
        </div>
      </div>`;
    } else if (type === "size" && facet.sizeTypes?.length) {
      inner = facet.sizeTypes
        .flatMap((st) =>
          (st.formats ?? []).flatMap((fmt) =>
            (fmt.values ?? []).map((v) =>
              this.#cbItem(
                id,
                String(v.id ?? ""),
                String(v.label ?? v.id ?? ""),
                v.count ?? null,
                av
              )
            )
          )
        )
        .join("");
    } else if (type === "color") {
      const vals = facet.values ?? [];
      inner = vals
        .map((v) =>
          this.#colorItem(
            id,
            String(v.id ?? ""),
            String(v.label ?? v.id ?? ""),
            String(v.color ?? ""),
            v.count ?? null,
            av
          )
        )
        .join("");
    } else {
      const vals = facet.values ?? facet.items ?? facet.options ?? [];
      inner = vals
        .map((v) =>
          this.#cbItem(
            id,
            String(v.value ?? v.id ?? ""),
            String(v.label ?? v.value ?? ""),
            v.count ?? v.docCount ?? null,
            av
          )
        )
        .join("");
    }

    let valuesClass = "filter-group__values";
    if (type === "color") valuesClass += " filter-group__values--color";
    else if (type !== "range") valuesClass += " filter-group__values--toggles";

    return `<div class="filter-group" data-filter-group data-group-id="${this.#escA(id)}">
      <button class="filter-group__toggle" type="button" data-toggle aria-expanded="false">
        <span>${this.#esc(label)}</span>
        ${cnt ? `<span class="filter-group__active-count">${cnt}</span>` : ""}
        ${CHEVRON_SVG}
      </button>
      <div class="${valuesClass}">${inner}</div>
    </div>`;
  }

  #colorItem(fid, val, lbl, hex, count, av) {
    const checked = Array.isArray(av) && av.includes(val);
    const zero = count === 0 && !checked;
    return `<label class="filter-color${zero ? " filter-color--zero" : ""}">
      <input type="checkbox" data-filter-type="checkbox" data-facet-id="${this.#escA(fid)}" value="${this.#escA(val)}" ${checked ? "checked" : ""} ${zero ? "disabled" : ""}>
      <span class="filter-color__name">${this.#esc(lbl)}</span>
      <span class="filter-color__dot" style="background-color:${this.#escA(hex)}" aria-hidden="true"></span>
    </label>`;
  }

  #cbItem(fid, val, lbl, count, av) {
    const checked = Array.isArray(av) && av.includes(val);
    const zero = count === 0 && !checked;
    return `<label class="filter-checkbox${zero ? " filter-checkbox--zero" : ""}">
      <input type="checkbox" data-filter-type="checkbox" data-facet-id="${this.#escA(fid)}" value="${this.#escA(val)}" ${checked ? "checked" : ""} ${zero ? "disabled" : ""}>
      <span class="filter-checkbox__label">
        ${this.#esc(lbl)}
        ${count !== null ? `<span class="filter-checkbox__count">(${count})</span>` : ""}
      </span>
    </label>`;
  }

  // Returns the human-readable label for a raw filter value (handles size IDs like ";NO_FORMAT[SML];XXS")
  #displayLabel(facetId, value) {
    for (const facet of this.#facets) {
      const fid = String(facet.id ?? facet.attribute ?? facet.label ?? "");
      if (fid !== facetId) continue;
      if (facet.sizeTypes?.length) {
        for (const st of facet.sizeTypes) {
          for (const fmt of st.formats ?? []) {
            for (const v of fmt.values ?? []) {
              if (String(v.id ?? "") === value) return String(v.label ?? value);
            }
          }
        }
      }
      const vals = facet.values ?? facet.items ?? facet.options ?? [];
      for (const v of vals) {
        if (String(v.id ?? v.value ?? "") === value) return String(v.label ?? value);
      }
    }
    return value;
  }

  #renderActivePills() {
    const container = document.getElementById("voyado-active-filters");
    if (!container) return;
    const pills = [];
    for (const [id, val] of Object.entries(this.#active)) {
      if (Array.isArray(val)) {
        for (const v of val) {
          const label = this.#displayLabel(id, v);
          pills.push(
            `<button class="active-filter-pill" type="button" data-action="remove-filter" data-facet-id="${this.#escA(id)}" data-value="${this.#escA(v)}">${this.#esc(label)} ×</button>`
          );
        }
      } else if (val && (val.min != null || val.max != null)) {
        const lbl = `${val.min ?? ""}–${val.max ?? ""} kr`;
        pills.push(
          `<button class="active-filter-pill" type="button" data-action="remove-range" data-facet-id="${this.#escA(id)}">${this.#esc(lbl)} ×</button>`
        );
      }
    }
    container.innerHTML = pills.length
      ? pills.join("") +
        `<button class="active-filter-pill active-filter-pill--clear" type="button" data-action="clear-all">Tøm alle</button>`
      : "";
  }

  #updateFilterBadge() {
    const badges = document.querySelectorAll("[data-filter-badge]");
    if (!badges.length) return;
    const count = Object.values(this.#active).reduce(
      (sum, v) =>
        sum +
        (Array.isArray(v)
          ? v.length
          : v && (v.min != null || v.max != null)
            ? 1
            : 0),
      0
    );
    badges.forEach((badge) => {
      badge.textContent = count || "";
      badge.hidden = count === 0;
    });
  }

  #hasActive() {
    return Object.values(this.#active).some((v) =>
      Array.isArray(v) ? v.length > 0 : v && (v.min != null || v.max != null)
    );
  }

  // ── Events ────────────────────────────────────────────────────────────────

  #bindEvents() {
    // Filter drawer body — change events for checkboxes, radios, range inputs
    const body = document.getElementById("filter-drawer-body");
    if (body) {
      this.#drawerBody = body;
      this.#onDrawerChange = (e) => {
        const { filterType } = e.target.dataset ?? {};
        if (filterType === "sort") {
          this.#sort = e.target.value || null;
          this.#apply();
        } else if (filterType === "checkbox") {
          this.#rebuildActive();
          this.#apply();
        }
      };
      this.#onDrawerFocusout = (e) => {
        if (e.target.dataset.filterType !== "range") return;
        const group = e.target.closest(".filter-price");
        if (group && (!e.relatedTarget || !group.contains(e.relatedTarget))) {
          this.#rebuildActive();
          this.#apply();
        }
      };
      this.#onDrawerKeydown = (e) => {
        if (e.key === "Enter" && e.target.dataset.filterType === "range") {
          this.#rebuildActive();
          this.#apply();
        }
      };
      body.addEventListener("change", this.#onDrawerChange);
      body.addEventListener("focusout", this.#onDrawerFocusout);
      body.addEventListener("keydown", this.#onDrawerKeydown);
    }

    // Active filter pills container (outside drawer)
    this.#onPillsClick = (e) => {
      const btn = e.target.closest("[data-action]");
      if (!btn) return;
      const { action, facetId, value } = btn.dataset;
      if (action === "clear-all") {
        this.#active = {};
        this.#sort = null;
      } else if (action === "remove-filter") {
        this.#active[facetId] = (this.#active[facetId] ?? []).filter(
          (v) => v !== value
        );
        if (!this.#active[facetId].length) delete this.#active[facetId];
      } else if (action === "remove-range") {
        delete this.#active[facetId];
      }
      this.#apply();
    };
    document
      .getElementById("voyado-active-filters")
      ?.addEventListener("click", this.#onPillsClick);

    // Back/forward navigation
    this.#onPopState = () => {
      this.#readURL(new URLSearchParams(location.search));
      this.#resetGrid();
      this.#fetch();
    };
    window.addEventListener("popstate", this.#onPopState);

    // ATC — store variant ticket before form submits
    this.#grid.addEventListener("click", (e) => {
      const btn = e.target.closest(".product-card__atc[data-voyado-variant-ticket]");
      if (!btn) return;
      try { sessionStorage.setItem("voyado_pending_atc_ticket", btn.dataset.voyadoVariantTicket); } catch {}
    }, { capture: true });

    // Gallery navigation
    this.#grid.addEventListener("click", (e) => {
      const btn = e.target.closest(".product-card__gallery-prev, .product-card__gallery-next");
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const link = btn.closest("[data-gallery]");
      if (!link) return;
      const imgs = JSON.parse(link.dataset.gallery);
      const dir = btn.classList.contains("product-card__gallery-next") ? 1 : -1;
      const cur = Number(link.dataset.galleryIndex ?? 0);
      const next = (cur + dir + imgs.length) % imgs.length;
      link.dataset.galleryIndex = next;
      const imgEl = link.querySelector(".product-card__img");
      if (imgEl) imgEl.src = imgs[next];
    }, { capture: true });

    // Click tracking
    this.#grid.addEventListener("click", (e) => {
      const link = e.target.closest("a[data-voyado-ticket]");
      if (link) this.#sendClick(link.dataset.voyadoTicket);
    });
  }

  #rebuildActive() {
    const body = document.getElementById("filter-drawer-body");
    if (!body) return;
    this.#active = {};
    body
      .querySelectorAll('input[data-filter-type="checkbox"]:checked')
      .forEach((cb) => {
        (this.#active[cb.dataset.facetId] ??= []).push(cb.value);
      });
    body.querySelectorAll('input[data-filter-type="range"]').forEach((inp) => {
      const { facetId: id, rangeBound: b } = inp.dataset;
      const v = inp.value.trim();
      if (!v) return;
      if (Array.isArray(this.#active[id])) this.#active[id] = {};
      (this.#active[id] ??= {})[b] = Number(v);
    });
  }

  #apply() {
    this.#pushURL();
    this.#resetGrid();
    this.#fetch();
  }

  #resetGrid() {
    this.#skip = 0;
    this.#done = false;
    this.#rendered = 0;
    this.#removeSentinel();
    // Keep old cards visible until new results arrive — clears in #render() at skip 0
    this.#grid.setAttribute('data-loading', '');
  }

  // ── Infinite scroll sentinel ──────────────────────────────────────────────

  #addSentinel() {
    this.#removeSentinel();
    const s = document.createElement("li");
    s.id = "voyado-sentinel";
    s.style.cssText =
      "grid-column:1/-1;height:1px;visibility:hidden;contain:strict";
    this.#grid.appendChild(s);
    this.#sentinel = s;
    this.#observer = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          this.#observer.disconnect();
          this.#fetch();
        }
      },
      { rootMargin: "300px" }
    );
    this.#observer.observe(s);
  }

  #removeSentinel() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#sentinel?.remove();
    this.#sentinel = null;
  }

  // ── States ────────────────────────────────────────────────────────────────

  #showEmpty() {
    this.#grid.removeAttribute('data-loading');
    this.#grid.innerHTML = `<li style="grid-column:1/-1;text-align:center;padding:48px"><p>${this.#esc(EMPTY_TEXT)}</p></li>`;
  }

  #showError() {
    this.#grid.removeAttribute('data-loading');
    this.#grid.querySelectorAll(".voyado-skeleton").forEach((el) => el.remove());
    this.#grid.innerHTML = `<li style="grid-column:1/-1;text-align:center;padding:48px">${this.#esc(ERROR_TEXT)}</li>`;
  }

  // ── Product card ──────────────────────────────────────────────────────────

  #card(p, i) {
    const url = this.#safeUrl(p.link);
    const title = p.title ?? "";
    const vendor = p.brand ?? "";
    const sp = p.sellingPrice?.min ?? null;
    const lp = p.listPrice?.min ?? null;
    const sale = lp !== null && sp !== null && Number(lp) > Number(sp);
    const ticket = p.ticket ?? "";
    const atcVariant = p.variants?.find(v => v.inStock) ?? p.variants?.[0];
    const atcVariantTicket = atcVariant?.ticket ?? "";
    const imageInfo = p.imageInfo ?? {};
    const effect = imageInfo.effect ?? "NONE";
    const images = imageInfo.images ?? [];
    const rawIdle = images[0]?.sources?.[0]?.url ?? null;
    const img = rawIdle ? this.#resized(this.#safeUrl(rawIdle.startsWith("http") ? rawIdle : `https:${rawIdle}`)) : null;
    const isSwap = effect === "SWAP";
    const isGallery = effect === "GALLERY";
    const rawHover = isSwap ? (images[1]?.sources?.[0]?.url ?? null) : null;
    const imgHover = rawHover ? this.#resized(this.#safeUrl(rawHover.startsWith("http") ? rawHover : `https:${rawHover}`)) : null;
    const galleryUrls = isGallery && images.length > 1
      ? images.map(m => { const r = m?.sources?.[0]?.url ?? null; return r ? this.#resized(this.#safeUrl(r.startsWith("http") ? r : `https:${r}`)) : null; }).filter(Boolean)
      : [];
    const disc = sale ? Math.round((1 - Number(sp) / Number(lp)) * 100) : 0;
    const handle = new URL(this.#safeUrl(url), location.origin).pathname.split('/products/')[1]?.split('/')[0] ?? '';
    return `<div class="product-card">
      <div class="product-card__media">
        <a class="product-card__img-link" href="${this.#escA(url)}"${ticket ? ` data-voyado-ticket="${this.#escA(ticket)}"` : ""}${galleryUrls.length > 1 ? ` data-gallery="${this.#escA(JSON.stringify(galleryUrls))}" data-gallery-index="0"` : ""} tabindex="-1" aria-hidden="true">
          ${
            img
              ? `<img class="product-card__img" src="${this.#escA(img)}" alt="${this.#escA(title)}" loading="${i < 4 ? "eager" : "lazy"}" decoding="${i === 0 ? "sync" : "async"}"${i === 0 ? ' fetchpriority="high"' : ""}>${isSwap && imgHover ? `<img class="product-card__img--hover" src="${this.#escA(imgHover)}" alt="" aria-hidden="true">` : ""}${galleryUrls.length > 1 ? `<button class="product-card__gallery-prev" type="button" tabindex="-1" aria-label="Forrige bilde"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><polyline points="15 18 9 12 15 6"/></svg></button><button class="product-card__gallery-next" type="button" tabindex="-1" aria-label="Neste bilde"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><polyline points="9 18 15 12 9 6"/></svg></button>` : ""}`
              : '<div class="product-card__img-placeholder"></div>'
          }
        </a>
        ${this.#renderBadges(p, disc, sale)}
      </div>
      <div class="product-card__details">
        <div class="product-card__info-row">
          <div class="product-card__text">
            <div class="product-card__names">
              ${vendor ? `<p class="product-card__vendor">${this.#esc(vendor)}</p>` : ""}
              <a class="product-card__title-link" href="${this.#escA(url)}"${ticket ? ` data-voyado-ticket="${this.#escA(ticket)}"` : ""}>
                <p class="product-card__title">${this.#esc(title)}</p>
              </a>
            </div>
            <div class="product-card__prices">
              ${
                sale
                  ? `<p class="product-card__price product-card__price--sale">${this.#fmt(sp)}</p><p class="product-card__compare">${this.#fmt(lp)}</p>`
                  : sp !== null
                    ? `<p class="product-card__price">${this.#fmt(sp)}</p>`
                    : ""
              }
            </div>
          </div>
          <quick-buy data-product-url="/products/${this.#escA(handle)}" data-has-sizes="false" data-has-colors="false" data-color-option="0" data-size-option="0" data-first-color="">
            <form action="/cart/add" method="post" class="product-card__atc-form">
              <input type="hidden" name="id" value="">
              <input type="hidden" name="quantity" value="1">
              <button class="product-card__atc" type="submit" aria-label="Legg i handlevogn" data-variant-id=""${atcVariantTicket ? ` data-voyado-variant-ticket="${this.#escA(atcVariantTicket)}"` : ""}${!p.inStock ? ' disabled' : ''}>${PLUS_SVG}</button>
            </form>
          </quick-buy>
        </div>
      </div>
    </div>`;
  }

  #renderBadges(p, disc, sale) {
    const items = [];
    let hasSoldout = false;
    if (sale && disc) items.push(`<span class="product-card__badge">-${disc}%</span>`);
    for (const b of (p.badges?.primary ?? [])) {
      if (b.theme === "DISCOUNT" || !b.label) continue;
      if (b.attribute === "stock_number") {
        if (Number(b.label) === 0) {
          items.push(`<span class="product-card__badge">Utsolgt</span>`);
          hasSoldout = true;
        }
        continue;
      }
      items.push(`<span class="product-card__badge">${this.#esc(String(b.label))}</span>`);
    }
    for (const b of (p.badges?.secondary ?? [])) {
      if (!b.label) continue;
      items.push(`<span class="product-card__badge product-card__badge--theme2">${this.#esc(String(b.label))}</span>`);
    }
    if (!p.inStock && !hasSoldout) items.push(`<span class="product-card__badge product-card__badge--soldout">Utsolgt</span>`);
    return items.length ? `<div class="product-card__badges">${items.join("")}</div>` : "";
  }

  // ── Editorial tile ───────────────────────────────────────────────────────

  // Mirrors snippets/collection-tile.liquid's markup so snippet-collection-tile.css applies unchanged.
  #tileCard(t) {
    const url = this.#safeUrl(t.url);
    const img = t.image ? this.#resized(this.#safeUrl(t.image), 800) : null;
    const label = t.title || t.url || "";
    return `<div class="collection-tile" style="--tile-col-span: ${this.#escA(String(t.colSpan ?? "1"))}">
      <a class="collection-tile__link" href="${this.#escA(url)}" aria-label="${this.#escA(label)}">
        <div class="collection-tile__media">
          ${img ? `<img class="collection-tile__img" src="${this.#escA(img)}" alt="${this.#escA(t.alt || t.title || "")}" loading="lazy">` : ""}
        </div>
        ${
          t.title
            ? `<div class="collection-tile__details">
          <span class="collection-tile__title">${this.#esc(t.title)}</span>
          <span class="collection-tile__arrow" aria-hidden="true"><svg class="icon icon--arrow-right" width="14" height="14" aria-hidden="true" focusable="false"><use href="#icon-arrow-right"></use></svg></span>
        </div>`
            : ""
        }
      </a>
    </div>`;
  }

  // ── Click tracking ────────────────────────────────────────────────────────

  // notify.click sends with keepalive internally (survives link navigation)
  // and auto-attaches market + customerKey/sessionKey.
  #sendClick(ticket) {
    if (!ticket || !window.elevate?.notify) return;
    window.elevate.notify.click(ticket);
  }

  // ── Utils ─────────────────────────────────────────────────────────────────

  #fmt(v) {
    if (v == null) return "";
    const n =
      typeof v === "object" ? Number(v.min ?? v.amount ?? 0) : Number(v);
    return Math.round(n).toLocaleString("nb-NO") + " kr";
  }

  #esc(s) {
    return String(s).replace(
      /[&<>"]/g,
      (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]
    );
  }
  #escA(s) {
    return String(s).replace(/["']/g, (c) => (c === '"' ? "&quot;" : "&#39;"));
  }
  #safeUrl(u) {
    const s = String(u ?? "");
    return s.startsWith("/") ||
      s.startsWith("https://") ||
      s.startsWith("http://")
      ? s
      : "#";
  }

  // Cards render at most ~416px wide (4-col desktop grid), so 800px covers 2x
  // retina without shipping the master-resolution file Voyado's API returns.
  #resized(url, width = 800) {
    if (!url || url === "#") return url;
    return url.includes("?") ? `${url}&width=${width}` : `${url}?width=${width}`;
  }

}

customElements.define("voyado-collection", VoyadoCollection);
